// @flow

import * as R from "ramda";
import { Image, Tag } from "../common";
import { Link } from "react-router-dom";
import {
	type Theme,
	getColor,
	getSize,
	getWeight,
} from "views/resources/theme";
import { isOpen } from "lib/office";
import { pathOr } from "ramda";
import React, { useGlobal } from "reactn";
import Skeleton from "react-loading-skeleton";
import logos from "views/resources/logos/SVG";
import moment from "moment";
import styled, { type StyledComponent } from "styled-components";
import type { Office } from "types";

interface Props {
	office: ?Office;
	bg: boolean;
	abridged: boolean;
	desktop: boolean;
	disabled: boolean;
}

const OfficeCard = React.memo(
	({ office, bg, abridged, desktop, disabled }: Props) => {
		const now = moment.utc();
		const openTag = () =>
			isOpen(now, office) && <Tag type="success">Abierto</Tag>;
		const [categories] = useGlobal("categories");
		const availableTag = office.online ? (
			<Tag type="success">Conectado</Tag>
		) : (
			<Tag type="disabled">Desconectado</Tag>
		);

		const reservableTag = office ? (
			office.options &&
			!(office.options.hidden_reserves_label === true) && (
				<Tag type="secondary">Reservas habilitadas</Tag>
			)
		) : (
			<Tag type="disabled">
				<Skeleton />
			</Tag>
		);

		const convertDistance = (distance: number) =>
			distance > 1000
				? Math.round(distance / 1000)
				: Math.round(distance);
		// $FlowFixMe
		const distance = R.pathOr<number>(
			NaN,
			["distance", "distance"],
			office
		);
		// $FlowFixMe
		const unit = R.pathOr<"m">("m", ["distance", "unit"], office);
		let distance_string = "";
		if (!isNaN(distance)) {
			distance_string = `· ${convertDistance(distance)} ${unit}`;
		}

		type WrapperProps = { office: Office, children: any };
		const Wrapper = ({ office, children, disabled }: WrapperProps) => {
			if (disabled) return children;
			return <Link to={`offices/${office.slug}`}>{children}</Link>;
		};
		const logo = pathOr(false, ["options", "brand", "logo"], office);
		const showLogo = pathOr(false, ["options", "brand", "show_logo"], office);

		return office ? (
			<Wrapper office={office} disabled={disabled}>
				<Card bg={bg} disabled={disabled}>
					<Left>
						<Image desktop={desktop}>
							<img
								src={
									showLogo
										? logo
										: logos[
												`logo_${office.category_id}_color`
										  ]
								}
							/>
						</Image>
					</Left>
					<View>
						<Title>
							{disabled && "[Oficina deshabilitada] - "}
							{office.name}
						</Title>
						{!abridged && (
							<>
								<Row>
									{openTag()}
									{availableTag}
									<Text>{office.options.hours_message}</Text>
									{office.online}
								</Row>
								<Row>
									<Text>
										{office.street} {distance_string}
									</Text>
								</Row>
							</>
						)}
						{reservableTag}
					</View>
				</Card>
			</Wrapper>
		) : (
			<SkeletonCard bg={bg} />
		);
	},
	(prevProps, props) => prevProps.hours !== props.hours
);

const SkeletonCard = ({ bg }: { bg: boolean }) => (
	<Card bg={bg}>
		<Left>
			<Image>
				<Skeleton />
			</Image>
		</Left>
		<View>
			<Title>
				<Skeleton />
			</Title>
			<Row>
				<Skeleton />
				<Text>
					<Skeleton />
				</Text>
			</Row>
			<Row>
				<Text>
					<Skeleton />
				</Text>
			</Row>
			<Skeleton />
		</View>
	</Card>
);

OfficeCard.defaultProps = {
	bg: false,
};

// $FlowFixMe
const officeLogo = (office) =>
	R.pathOr("", ["category", "slug"], office) + "Color";

const Card: StyledComponent<*, Theme, *> = styled.div`
  display: flex;
  flex-direction: row;
  background ${({ bg, theme }) => (bg ? theme.colors.bg_alt : theme.colors.bg)};
  padding: 0;
  position: relative;

${(p) =>
	p.disabled &&
	`
&:after {
	 position:absolute;
	 top: 0;
	 left:0;
	 width: 100%;
	 height: 100%;
	 background-color: rgba(255,255,255,0.85);
	 display: block;
	 content: " ";
}`}
`;

const View = styled.div`
	padding: ${getSize("m")} ${getSize("sm")};
`;

const Title = styled.h3`
	margin: ${getSize("s")} 0;
	font-size: ${getSize("m")};
	font-size: 16px;
	line-height: ${getSize("mm")};
	color: ${getColor("fg")};
`;

const Text = styled.p`
	margin: 0 0 ${getSize("xs")};
	font-size: ${getSize("sm")};
	font-size: 12px;
	line-height: ${getSize("mm")};
	color: ${getColor("fg")};
	font-weight: ${getWeight("medium")};
	display: inline-block;
`;

const Row = styled.div`
	color: #282828 !important;
`;

const Left = styled.div`
	padding: ${getSize("l")} 0.6rem;
	box-sizing: border-box;
`;

OfficeCard.width = window.screen.width;
OfficeCard.height = window.screen.height;
OfficeCard.size = 120;

export default OfficeCard;
